import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../shared/components/Button';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { ApiError } from '../../identity/types';
import { OnboardingLayout } from '../components/OnboardingLayout';
import { employeeOnboardingGateway } from '../index';
import { MISSING_SECTION_TARGETS, taxSectionPath, type EmployeeSubmission } from '../types';

/**
 * Step 3 (Upload Documents) is out of scope for this slice, so this is where the employee reviews what is still missing and SUBMITS the whole
 * onboarding. Until they submit, the administrator sees them as "Onboarding in Progress", not Active. What is complete is decided by the
 * server from what is saved; this page only shows it. Documents (the W-4 and I-9 uploads) are not required yet because that step does not exist.
 * UX / DESIGN GAP: there is no approved Figma for this review-and-submit step yet; it reuses the existing onboarding card.
 */
export function EmployeeDocumentsPlaceholderPage() {
  const navigate = useNavigate();
  const [submission, setSubmission] = useState<EmployeeSubmission | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      setSubmission(await employeeOnboardingGateway.getSubmission());
    } catch (err) {
      setLoadError(friendlyErrorMessage(err, 'We could not check your progress. Please try again.'));
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    employeeOnboardingGateway
      .getSubmission()
      .then((next) => { if (!cancelled) setSubmission(next); })
      .catch((err: unknown) => { if (!cancelled) setLoadError(friendlyErrorMessage(err, 'We could not check your progress. Please try again.')); });
    return () => { cancelled = true; };
  }, []);

  async function submit() {
    setSubmitError(null);
    setSubmitting(true);
    try {
      setSubmission(await employeeOnboardingGateway.submitOnboarding());
    } catch (err) {
      if (err instanceof ApiError && err.code === 'onboarding_incomplete') {
        // Something changed since the page loaded; show what is missing now.
        await load();
        setSubmitError('Some required sections are still incomplete. Finish them, then submit.');
      } else {
        setSubmitError(friendlyErrorMessage(err, 'We could not submit your information. Please try again.'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <OnboardingLayout activeStep={3}>
      <section className="eo-panel" aria-labelledby="eo-documents-title">
        <h2 id="eo-documents-title" className="eo-panel__title">
          Step 3: Upload Documents is next
        </h2>
        <p className="eo-note">Your Taxes &amp; Exemptions answers have been saved. The Upload Documents step is not available yet.</p>

        {loadError && (
          <p className="eo-banner eo-banner--error" role="alert">
            {loadError}
          </p>
        )}
        {!submission && !loadError && (
          <p className="eo-loading" role="status">
            Checking your progress…
          </p>
        )}

        {submission?.submitted && (
          <p className="eo-banner eo-banner--info" role="status" data-testid="onboarding-submitted">
            Your information has been submitted. Thank you.
          </p>
        )}

        {submission && !submission.submitted && (
          <div data-testid="onboarding-review">
            <h3 className="eo-panel__subtitle">Review and submit</h3>
            {submission.missing.length > 0 ? (
              <>
                <p className="eo-note">These sections still need your answers before you can submit:</p>
                <ul className="eo-missing">
                  {submission.missing.map((key) => (
                    <li key={key}>
                      <button type="button" className="eo-link-button" onClick={() => navigate(MISSING_SECTION_TARGETS[key].path)}>
                        {MISSING_SECTION_TARGETS[key].label}
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <p className="eo-note">Every required section is complete. Submit when you are ready.</p>
            )}
            {submitError && (
              <p className="eo-banner eo-banner--error" role="alert">
                {submitError}
              </p>
            )}
          </div>
        )}

        <div className="eo-footer">
          <span />
          <div className="eo-footer__right">
            <button type="button" className="eo-btn eo-btn--outline" onClick={() => navigate(taxSectionPath('tax-summary'))}>
              Go Back
            </button>
            {submission && !submission.submitted && (
              <Button
                type="button"
                className="eo-btn eo-btn--primary"
                disabled={!submission.canSubmit}
                loading={submitting}
                loadingLabel="Submitting…"
                onClick={() => void submit()}
              >
                Submit
              </Button>
            )}
          </div>
        </div>
      </section>
    </OnboardingLayout>
  );
}
